/**
 * 1010 建站舱流程改造 · 展示口径(纯函数 / 常量):图片来源人话、产品 / 案例位不用模板原图、新闻体裁、客服按钮写入说明。
 */
import type { FlowImageSlot, FlowImageSource, LaunchChat, NewsGenre } from "../api/flowTypes";

export const SOURCE_TEXT: Record<FlowImageSource, string> = {
  gallery: "你上传的图",
  upload: "你上传的图",
  ai: "AI 生成",
  template: "模板原图",
  product: "产品图",
  logo: "Logo",
  pending: "AI 没生成成功,待重试",
  empty: "未配图",
};

/**
 * 只有装饰 / 图标位能用模板原图。1024:模板复刻的是真实企业官网,其余位置的原图常带原企业门头、logo、菜品(与后端 HostImagePolicy 同口径)。
 */
export function templateAllowed(slot: Pick<FlowImageSlot, "category">): boolean {
  return slot.category === "decor";
}

export const NEWS_GENRES: ReadonlyArray<{ value: NewsGenre; label: string }> = [
  { value: "COMPANY_NEWS", label: "企业新闻" },
  { value: "BRAND_PROFILE", label: "品牌介绍" },
  { value: "EVENT_RELEASE", label: "活动通稿" },
  { value: "PRODUCT_COPY", label: "产品软文" },
];

export function chatModeText(chat: LaunchChat): string {
  switch (chat.mode) {
    case "buttons":
      return `已把站点上 ${chat.linkedButtons} 个「在线咨询 / 联系我们」类按钮改成客服链接`;
    case "nav":
      return "站点上没找到咨询按钮,已加到导航最后一项";
    case "link_only":
      return "没能写进站点按钮,先用下面的链接 / 二维码";
    default:
      return "";
  }
}

// ── 换图面板(1010 演示机反馈)──

export type SlotKind = "logo" | "qrcode" | "normal";

/** Logo / 二维码位单独处理:不给 AI 重新生成,给一句去哪弄。 */
export function slotKind(slot: Pick<FlowImageSlot, "category" | "role">): SlotKind {
  if (slot.category === "qrcode") return "qrcode";
  if (slot.category === "logo" || slot.role === "img-logo") return "logo";
  return "normal";
}

export const SLOT_KIND_HINT: Record<Exclude<SlotKind, "normal">, string> = {
  logo: "Logo 去品牌资产上传",
  qrcode: "接入客服后自动生成",
};

/** 品牌资产页(Logo 在那上传)。 */
export const BRAND_ASSETS_PATH = "/tenant-admin/brand-assets";

export function canRegenerate(
  slot: Pick<FlowImageSlot, "category" | "role" | "aiEligible">
): boolean {
  return slot.aiEligible && slotKind(slot) === "normal";
}

export type SlotTagTone = "ai" | "template" | "own" | "none";

/** 每格的四种标签:AI 生成 / 模板原图 / 你的图 / 未配图。 */
export function slotTag(slot: Pick<FlowImageSlot, "source" | "url">): {
  label: string;
  tone: SlotTagTone;
} {
  switch (slot.source) {
    case "ai":
      return { label: "AI 生成", tone: "ai" };
    case "template":
      return { label: "模板原图", tone: "template" };
    case "gallery":
    case "upload":
    case "product":
    case "logo":
      return { label: "你的图", tone: "own" };
    default:
      return { label: "未配图", tone: "none" };
  }
}

// ── 计费 / 站点状态(1010 演示机第二轮)──

/**
 * AI 配图单价(SKU `website.ai_image`,V24,契约:28 积分 / 张)。只用于不方便请求报价的静态说明;
 * 真正扣多少以报价接口 `unitCredits` 为准(生成页 / 换图都用报价口的数)。
 */
export const AI_IMAGE_UNIT_CREDITS = 28;

/** 建站舱积分说明(站点列表右栏)。 */
export function websiteBillingText(unitCredits: number = AI_IMAGE_UNIT_CREDITS): string {
  return `积分消耗:文案生成与发布不扣积分;AI 配图按张扣积分(${unitCredits} 积分 / 张),生成前会先报价`;
}

/**
 * 站点卡状态标(按 launched,不按 status):已归档 > 已上线(launched)> 预览中(有预览版本)> 草稿。
 * 老后端没给 launched 时按 status != draft。
 */
export function siteStatusLabel(site: {
  status: string;
  launched?: boolean;
  currentVersionNo: number | null;
}): "已归档" | "已上线" | "预览中" | "草稿" {
  if (site.status === "archived") return "已归档";
  const launched = typeof site.launched === "boolean" ? site.launched : site.status !== "draft";
  if (launched) return "已上线";
  if (site.currentVersionNo != null) return "预览中";
  return "草稿";
}
